import { useState } from "react";
import { Alert, Button, Stack, TextField, Typography } from "@mui/material";
import { useAuth } from "@/auth/AuthContext";
import { ApiError, post } from "@/api/client";
import AuthLayout from "@/brand/AuthLayout";

/**
 * Shown instead of the application while the signed-in account owes a password
 * change. The server refuses everything except this screen's own calls, so
 * there is nothing to skip to — the redirect is a courtesy, not the control.
 */
export default function ChangePasswordPage() {
  const { me, refresh, logout } = useAuth();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (next !== confirm) {
      setError("The two new passwords do not match");
      return;
    }
    setBusy(true);
    try {
      await post("/auth/password", { current_password: current, new_password: next });
      await refresh();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Something went wrong");
    } finally {
      setBusy(false);
    }
  }

  return (
    <AuthLayout>
      <Stack component="form" onSubmit={submit} spacing={2.5}>
        <div>
          <Typography variant="h5" fontWeight={600}>
            Choose a new password
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {me?.user.email} was set up with a password that was handed over to
            you. Replace it before using the Console.
          </Typography>
        </div>

        {error && <Alert severity="error">{error}</Alert>}

        <TextField
          label="Current password"
          type="password"
          value={current}
          onChange={(e) => setCurrent(e.target.value)}
          autoFocus
          autoComplete="current-password"
        />
        <TextField
          label="New password"
          type="password"
          value={next}
          onChange={(e) => setNext(e.target.value)}
          autoComplete="new-password"
        />
        <TextField
          label="New password again"
          type="password"
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
          autoComplete="new-password"
        />

        <Button type="submit" variant="contained" size="large" disabled={busy}>
          {busy ? "Saving…" : "Set password"}
        </Button>
        <Button onClick={() => void logout()} size="small" color="inherit">
          Sign out
        </Button>
      </Stack>
    </AuthLayout>
  );
}
